import React, { useCallback, useMemo, useState } from "react";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ReactSelect from "react-select";
import CreatableSelect from 'react-select/creatable';
import classNames from "classnames";
import Tooltip from "@material-ui/core/Tooltip";

import { Checkbox } from "../Checkbox/Checkbox";

import "./Select.scss";

const customStyles = {
    menuPortal: (styles) => ({ ...styles, zIndex: 1500 }),
    input: (base) => ({
        ...base,
        padding: 0,
        margin: 0
    })
};


const DropdownIndicator = ({ isFocused }) =>  {
    if (isFocused) {
        return (
            <div className="select__dropdown-arrow">
                <ExpandLessIcon className="select__dropdown-icon"/>
            </div>
        );
    }

    return (
        <div className="select__dropdown-arrow">
            <ExpandMoreIcon className="select__dropdown-icon"/>
        </div>
    );
}

const MultiValue = ({ data }) => (
    data && <div className="select__multi-value">{data.label}</div>
);

const MenuList = (props) => {
    const { isMulti, options, getValue, selectOption, selectProps } = props;
    const { inputValue } = selectProps;

    const filteredOptions = useMemo(() => options.filter(
        (item) => (inputValue ? String(item.label.toLowerCase()).indexOf(String(inputValue).toLowerCase()) !== -1 : item)
    ), [ inputValue, options ]);

    if (!filteredOptions.length) {
        return <div>No Items</div>;
    }

    return (
        <div className="select__menu-list">
            {
                filteredOptions.length ? filteredOptions.map((option) => {
                    const isSelected = getValue().indexOf(option) !== -1;

                    return (
                        <div
                            key={option.value}
                            className={classNames(
                                "select__option",
                                {
                                    "select__option--is-selected": isSelected,
                                    "select__option--multi": isMulti
                                }
                            )}
                            role="button"
                            tabIndex={0}
                            onClick={() => selectOption(option)}
                        >
                            {isMulti && (
                                <Checkbox
                                    className="select__option-checkbox"
                                    value={isSelected}
                                />
                            )}


                            <Tooltip title={option.label || ""}>
                                <div className="select__option-label">
                                    {option.label}
                                </div>
                            </Tooltip>
                        </div>
                    );
                }) : <div>No Items</div>
            }
        </div>
    );
};

export const Select = ({
    className,
    classNameSelect,
    label,
    value,
    options = [],
    error,
    helperText,
    isMulti = false,
    isMultiCheckbox = false,
    hideHelpText = false,
    isClearable = false,
    isSearchable = false,
    isMenuPortal = false,
    isCreatable = false,
    isLoading,
    isDisabled,
    onChange,
    ...restProps
}) => {
    const [ inputValue, setInputValue ] = useState("");

    const currentValue = useMemo(() => {
        if (Array.isArray(value)) {
            return value.map((item) => options.find((option) => option.value === item));
        }

        if (Array.isArray(options) ?? value) {
            return options.find((option) => option.value === String(value) || option.value === value);
        }

        return value;
    }, [ value, options ]);

    const handleChange = useCallback((optionValue) => {
        if (Array.isArray(optionValue)) {
            const preparedOptionValue = optionValue.map((item) => item.value);

            onChange(preparedOptionValue);
        } else {
            onChange(optionValue ? optionValue.value : optionValue);
        }
    }, [ onChange ]);

    const handleInputChange = useCallback((value, { action }) => {
        if (action !== "set-value"){
            setInputValue(value);
        }
    }, [ setInputValue ]);


    let rootProps = {
        ...restProps,
        menuPlacement: "auto",
        className: classNames("select__body", classNameSelect),
        classNamePrefix: "select",
        options: options,
        isMulti: isMulti,
        closeMenuOnSelect: !isMulti,
        blurInputOnSelect: !isMulti,
        isSearchable: isSearchable,
        isLoading: isLoading,
        isClearable: isClearable,
        inputValue: inputValue,
        onInputChange: handleInputChange,
        isDisabled: isDisabled || isLoading,
        value: currentValue || "",
        components: isMultiCheckbox ? {
            DropdownIndicator,
            MultiValue,
            MenuList,
            ...restProps.components
        } : {
            DropdownIndicator,
        },
        onBlur: () => {},
        onChange: handleChange
    };

    if (isMenuPortal) {
        rootProps = {
            ...rootProps,
            styles: customStyles,
            menuPortalTarget: document.body
        };
    }

    return (
        <div
            className={
                classNames(
                    "select",
                    { "select--helper-message": helperText },
                    { "select--error": error },
                    className
                )
            }
        >
            { label && (<div className="select__label">{ label }</div>) }

            { isCreatable ? <CreatableSelect {...rootProps} /> : <ReactSelect {...rootProps} /> }

            { !hideHelpText && <div className="select__helper-message">{ error || helperText }</div> }
        </div>
    );
};
